/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: watermark-asset-service.ts
 *
 * Created by kyang on 10/21/20.
 */

// This service is an implementation for BrandingAssetService.
// Components should invoke the abstract BrandingAssetService, not this specific implementation.
//
// BrandingAssetService implementations work on
// the root asset object to display in the editor.
// Artwork needs to displayed as a list of unrelated assets,
// So, use the list of artworks as the "root asset object" instead.

angular.module('prism-ui.components.branding').service('ArtworkAssetService', function (
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    ArtworkResourceFactory: JS5Object,
    BrandingAssetService: JS5Object,
    BrandingViewService: JS5Object,
    MediaProfileResourceFactory: JS5Object,
) {

    //==========================//
    // Branding Asset Container //
    //==========================//

    this.getBrandingAssetContainer = (containerId: string) => {
        return ArtworkResourceFactory.get({containerId}).$promise;
    };

    this.parseBrandingAssetContainer = (brandingConfig: JS5Object, brandingAssetContainer: JS5Object) => {
        brandingAssetContainer.assets = BrandingAssetService.filterBrandingAssets(brandingConfig, brandingAssetContainer.assets, {anySeasonVolume: true});
    };

    this.updateBrandingAssetContainer = (brandingConfig: JS5Object, brandingAssetContainer: JS5Object) => {
        return ArtworkResourceFactory.update({
            containerId: brandingAssetContainer.id
        }, brandingAssetContainer).$promise;
    };


    //=======//
    // Asset //
    //=======//

    const parseArtworkDTO = (asset: JS5Object) => {
        if (!asset.language) {
            asset.language = {
                id: -1,
                name: '[None]'
            };
        }
        return asset;
    };

    const createRootArtworkAsset = (
        containerId: string,
        seasonId: string,
        volumeId: string,
        artworkType: string,
        acceptProfile: JS5Object,
        artworkAssets: JS5Object[]
    ) => {
        return {
            id: containerId,
            seasonId: seasonId,
            volumeId: volumeId,
            acceptProfile: acceptProfile,
            assets: _.cloneDeep(artworkAssets),
            type: artworkType,
            originalAssetsById: _.keyBy(artworkAssets, 'id'),
        };
    };

    this.getRootBrandingAsset = (brandingConfig: JS5Object, container: JS5Object, acceptProfile: JS5Object) => {
        let artworkAssets = BrandingAssetService.filterBrandingAssets(brandingConfig, container.assets)
            .filter((artwork: JS5Object) => artwork.acceptProfile.id === acceptProfile.id)
            .map(parseArtworkDTO);
        let artworkType = 'Artwork';
        if (brandingConfig.seasonId) {
            artworkType = 'SeasonArtwork';
        } else if (brandingConfig.volumeId) {
            artworkType = 'VolumeArtwork';
        }
        return createRootArtworkAsset(
            container.id,
            brandingConfig.seasonId,
            brandingConfig.volumeId,
            artworkType,
            _.get(artworkAssets, '0.acceptProfile', acceptProfile),
            artworkAssets
        );
    };

    this.validateNonTextAsset = (acceptProfileId: number, asset: JS5Object, virtualPath: string) => {
        return MediaProfileResourceFactory.validate({
            path: virtualPath,
            profileId: acceptProfileId
        }).$promise.then((validationResult: JS5Object) => {
            return _.get(validationResult, `${acceptProfileId}.failures`);
        });
    };

    this.createRootBrandingAsset = (brandingConfig: JS5Object, rootAsset: JS5Object) => {
        return $q.reject({
            description: 'Artworks use mock rootBrandingAssets, which cannot be created by this function'
        });
    };

    const saveAssets = (brandingConfig: JS5Object, assets: JS5Object[], originalAssetsById: JS5Object) => {
        let savePromises = _.map(assets, (asset: JS5Object) => {
                let savePromise: JS5Promise;
                if (_.get(asset, 'language.id') === -1) {
                    asset.language = null;
                }
                if (asset.id && angular.equals(asset, originalAssetsById[asset.id])) {
                    savePromise = $q.when(asset);
                } else if (asset.id) {
                    savePromise = ArtworkResourceFactory.updateAsset({assetId: asset.id}, asset).$promise
                } else {
                    savePromise = ArtworkResourceFactory.createAsset({containerId: brandingConfig.id}, asset).$promise
                }
                return savePromise.then(parseArtworkDTO);
            });
        return $q.all(savePromises);
    };

    this.updateRootBrandingAsset = (brandingConfig: JS5Object, rootAsset: JS5Object) => {
        // TODO: When API is updated, make this one API call
        let deleteAssetPromises = _.chain(rootAsset.originalAssetsById)
            .filter((originalAsset: JS5Object) => {
                return !_.find(rootAsset.assets, {id: originalAsset.id});
            }).map((originalAsset: JS5Object) => {
                return ArtworkResourceFactory.deleteAsset({assetId: originalAsset.id}).$promise;
            }).value();
        let saveAssetsPromise = saveAssets(brandingConfig, rootAsset.assets, rootAsset.originalAssetsById);
        return $q.all([...deleteAssetPromises, saveAssetsPromise])
            .then((data: JS5Object[]) => {
                return createRootArtworkAsset(
                    rootAsset.id,
                    rootAsset.seasonId,
                    rootAsset.volumeId,
                    rootAsset.type,
                    rootAsset.acceptProfile,
                    _.last(data)
                );
            });
    };


    //=========//
    // Proxies //
    //=========//

    this.hasVideoAssetProxyStatus = (brandingConfig: JS5Object, rootAsset: JS5Object) => {
        return _.some(rootAsset.assets, (artworkAsset: JS5Object) => {
            return !BrandingAssetService.assetWasIngested(artworkAsset);
        });
    };

    const getArtworkAssetProxyStatus = (updatedArtworkAsset: JS5Object) => {
        if (BrandingAssetService.assetHasProxy(updatedArtworkAsset)) {
            return BrandingAssetService.ProxyStatus.PROXY_COMPLETE;
        } else if (_.get(updatedArtworkAsset, 'ingestInfo.workflowStatus') === BrandingAssetService.ProxyStatus.PROXY_STALLED) {
            return BrandingAssetService.ProxyStatus.PROXY_STALLED;
        }
        return BrandingAssetService.ProxyStatus.PROXY_RUNNING;
    };

    this.updateVideoAssetProxyStatus = (brandingConfig: JS5Object, rootAsset: JS5Object, intervalData: JS5Object) => {
        return this.getBrandingAssetContainer(brandingConfig.id)
            .then((updatedContainer: JS5Object) => {
                let updatedAsset = this.getRootBrandingAsset(brandingConfig, updatedContainer, rootAsset.acceptProfile);
                let finalState = BrandingAssetService.ProxyStatus.PROXY_COMPLETE;
                _.forEach(updatedAsset.assets, (artworkAsset: JS5Object) => {
                    let updatedStatus = getArtworkAssetProxyStatus(artworkAsset);
                    let prevStatus = _.get(intervalData, artworkAsset.id, updatedStatus);
                    if (updatedStatus !== prevStatus) {
                        BrandingViewService.showVideoIngestStatusNotifications(brandingConfig, artworkAsset, updatedStatus, `- ${artworkAsset.language.name} `);
                    }
                    switch (updatedStatus) {
                        case BrandingAssetService.ProxyStatus.PROXY_COMPLETE:
                            break;
                        case BrandingAssetService.ProxyStatus.PROXY_STALLED:
                            if (finalState === BrandingAssetService.ProxyStatus.PROXY_COMPLETE) {
                                finalState = BrandingAssetService.ProxyStatus.PROXY_STALLED;
                            }
                            break;
                        case BrandingAssetService.ProxyStatus.PROXY_RUNNING:
                            finalState = BrandingAssetService.ProxyStatus.PROXY_RUNNING;
                    }
                    intervalData[artworkAsset.id] = updatedStatus;
                });

                return finalState;
            });
    };
});


//=================//
// Lifecycle Hooks //
//=================//

// Load this service when app is initialized
angular.module('prism-ui.components.branding').run((
    BrandingAssetService: JS5Object,
    ArtworkAssetService: JS5Object,
) => {
    BrandingAssetService.registerAssetServiceImplementation('artwork', ArtworkAssetService);
});
